import {
  add,
  angleDelta,
  clamp,
  dot,
  headingVector,
  length,
  lengthSquared,
  normalize,
  rightVector,
  scale,
  subtract,
} from './math'
import type { CombatHit, ProjectileState, Vec2, VehicleState } from './types'
import { WEAPON_MOUNT } from './weapon-mount'

export const MAGAZINE_CAPACITY = 12
export const RELOAD_SECONDS = 1.35
export const HITS_TO_STUN = 4
export const FIRE_COOLDOWN = 0.18
const DRIVER_STUN_SECONDS = 0.65
const HIT_IMMUNITY_SECONDS = 0.35
const HIT_MOMENTUM_RETENTION = 0.94
const PROJECTILE_SPEED = 96
const PROJECTILE_TTL = 1.8
const TARGET_RADIUS = 1.8
const AIM_RANGE = 42
const AIM_CONE = 0.42
const AIM_CONVERGENCE_DISTANCE = 28
export const MAX_AIM_ASSIST = 0.18

export interface FireResult {
  vehicle: VehicleState
  projectiles: ProjectileState[]
  fired: boolean
}

export interface HitResolution {
  vehicles: VehicleState[]
  projectiles: ProjectileState[]
  hits: CombatHit[]
}

export function applyDriverHit(vehicle: VehicleState): { vehicle: VehicleState; accepted: boolean; stunned: boolean } {
  if (vehicle.driverStunTimer > 0 || vehicle.hitImmunityTimer > 0) {
    return { vehicle, accepted: false, stunned: false }
  }
  const hitCount = vehicle.driverHitCount + 1
  const stunned = hitCount >= HITS_TO_STUN
  return {
    accepted: true,
    stunned,
    vehicle: {
      ...vehicle,
      driverHitCount: stunned ? 0 : hitCount,
      driverStunTimer: stunned ? DRIVER_STUN_SECONDS : vehicle.driverStunTimer,
      hitImmunityTimer: stunned ? HIT_IMMUNITY_SECONDS : vehicle.hitImmunityTimer,
      velocity: stunned ? scale(vehicle.velocity, HIT_MOMENTUM_RETENTION) : vehicle.velocity,
    },
  }
}

export function getMuzzlePosition(vehicle: VehicleState): Vec2 {
  const forward = headingVector(vehicle.heading)
  const driverSide = rightVector(vehicle.heading)
  return add(vehicle.position, add(scale(forward, WEAPON_MOUNT.muzzle[2]), scale(driverSide, WEAPON_MOUNT.muzzle[0])))
}

export function selectAimTarget(shooter: VehicleState, vehicles: readonly VehicleState[]): VehicleState | null {
  let selected: VehicleState | null = null
  let selectedScore = Number.POSITIVE_INFINITY

  for (const target of vehicles) {
    if (target.id === shooter.id) continue
    const offset = subtract(target.position, shooter.position)
    const range = length(offset)
    if (range < 3 || range > AIM_RANGE) continue
    const targetHeading = Math.atan2(offset.x, offset.y)
    const angularError = Math.abs(angleDelta(shooter.heading, targetHeading))
    if (angularError > AIM_CONE || dot(normalize(offset), headingVector(shooter.heading)) <= 0) continue
    const score = angularError * AIM_RANGE + range / AIM_RANGE
    if (score >= selectedScore) continue
    selected = target
    selectedScore = score
  }

  return selected
}

export function getShotDirection(vehicle: VehicleState, vehicles: readonly VehicleState[]): Vec2 {
  const forward = headingVector(vehicle.heading)
  const muzzle = getMuzzlePosition(vehicle)
  const target = selectAimTarget(vehicle, vehicles)
  const aimPoint = target ? target.position : add(vehicle.position, scale(forward, AIM_CONVERGENCE_DISTANCE))
  const aimOffset = subtract(aimPoint, muzzle)
  if (lengthSquared(aimOffset) <= 1e-9) return forward
  const targetHeading = Math.atan2(aimOffset.x, aimOffset.y)
  const correction = clamp(angleDelta(vehicle.heading, targetHeading), -MAX_AIM_ASSIST, MAX_AIM_ASSIST)
  return headingVector(vehicle.heading + correction)
}

export function beginReload(vehicle: VehicleState): VehicleState {
  if (vehicle.reloadTimer > 0 || vehicle.ammo >= MAGAZINE_CAPACITY || vehicle.driverStunTimer > 0) return vehicle
  return { ...vehicle, reloadTimer: RELOAD_SECONDS }
}

export function tryFire(
  vehicle: VehicleState,
  projectiles: ProjectileState[],
  nextId: number,
  vehicles: readonly VehicleState[] = [vehicle],
): FireResult {
  if (vehicle.ammo <= 0) {
    return { vehicle: beginReload(vehicle), projectiles, fired: false }
  }
  if (
    vehicle.weaponCooldown > 0 ||
    vehicle.fireSuppressionTimer > 0 ||
    vehicle.reloadTimer > 0 ||
    vehicle.driverStunTimer > 0
  ) {
    return { vehicle, projectiles, fired: false }
  }

  const direction = getShotDirection(vehicle, vehicles)
  const position = getMuzzlePosition(vehicle)
  const ammo = vehicle.ammo - 1
  const nextVehicle = {
    ...vehicle,
    ammo,
    weaponCooldown: FIRE_COOLDOWN,
    reloadTimer: ammo === 0 ? RELOAD_SECONDS : vehicle.reloadTimer,
  }
  const projectile: ProjectileState = {
    id: nextId,
    ownerId: vehicle.id,
    previous: { ...position },
    position,
    velocity: scale(direction, PROJECTILE_SPEED + Math.max(0, vehicle.speed)),
    ttl: PROJECTILE_TTL,
  }
  return { vehicle: nextVehicle, projectiles: [...projectiles, projectile], fired: true }
}

export function stepProjectiles(projectiles: ProjectileState[], dt: number): ProjectileState[] {
  const safeDt = Math.max(0, dt)
  return projectiles
    .map((projectile) => ({
      ...projectile,
      previous: { ...projectile.position },
      position: add(projectile.position, scale(projectile.velocity, safeDt)),
      ttl: projectile.ttl - safeDt,
    }))
    .filter((projectile) => projectile.ttl > 0)
}

export function sweptPointHitsCircle(start: Vec2, end: Vec2, center: Vec2, radius: number): boolean {
  const segment = subtract(end, start)
  const segmentLengthSquared = lengthSquared(segment)
  const amount =
    segmentLengthSquared <= 1e-9 ? 0 : clamp(dot(subtract(center, start), segment) / segmentLengthSquared, 0, 1)
  const closest = add(start, scale(segment, amount))
  return lengthSquared(subtract(center, closest)) <= radius * radius
}

export function resolveProjectileHits(projectiles: ProjectileState[], vehicles: VehicleState[]): HitResolution {
  const byId = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]))
  const hits: CombatHit[] = []
  const remaining: ProjectileState[] = []

  for (const projectile of projectiles) {
    let consumed = false
    for (const target of byId.values()) {
      if (
        target.id === projectile.ownerId ||
        !sweptPointHitsCircle(projectile.previous, projectile.position, target.position, TARGET_RADIUS)
      ) {
        continue
      }

      const result = applyDriverHit(target)
      if (result.accepted) byId.set(target.id, result.vehicle)
      hits.push({ ownerId: projectile.ownerId, targetId: target.id, stunned: result.stunned })
      consumed = true
      break
    }
    if (!consumed) remaining.push(projectile)
  }

  return {
    vehicles: vehicles.map((vehicle) => byId.get(vehicle.id) ?? vehicle),
    projectiles: remaining,
    hits,
  }
}

export function stepCombatTimers(vehicle: VehicleState, dt: number): VehicleState {
  const safeDt = Math.max(0, dt)
  const reloadTimer = Math.max(0, vehicle.reloadTimer - safeDt)
  const reloadCompleted = vehicle.reloadTimer > 0 && reloadTimer === 0
  return {
    ...vehicle,
    ammo: reloadCompleted ? MAGAZINE_CAPACITY : vehicle.ammo,
    reloadTimer,
    weaponCooldown: Math.max(0, vehicle.weaponCooldown - safeDt),
    fireSuppressionTimer: Math.max(0, vehicle.fireSuppressionTimer - safeDt),
    driverStunTimer: Math.max(0, vehicle.driverStunTimer - safeDt),
    hitImmunityTimer: Math.max(0, vehicle.hitImmunityTimer - safeDt),
  }
}
